import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { guides } from "@/lib/guides";
import { pathFor, useLocale, type Locale } from "@/lib/i18n";
import { absoluteUrl } from "@/lib/seo";

type Crumb = { label: string; to: string };

/** Härleder eventuell mellannivå, t.ex. Start › Guider › Sidan. */
function parentCrumb(pathname: string, locale: Locale): Crumb | null {
  const clean = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  const isGuide = guides.some((g) => pathFor(g.to, locale) === clean);
  if (isGuide) {
    return {
      label: locale === "en" ? "Guides" : locale === "es" ? "Guías" : "Guider",
      to: pathFor("/guider", locale),
    };
  }
  const hub = pathFor("/kunskapsbank", locale);
  if (clean.startsWith(`${hub}/`)) {
    return { label: locale === "en" ? "Knowledge hub" : locale === "es" ? "Centro de recursos" : "Kunskapsbank", to: hub };
  }
  return null;
}

function Breadcrumbs({ title: rawTitle, locale }: { title: string; locale: Locale }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const home = pathFor("/", locale);
  const homeLabel = locale === "en" ? "Home" : locale === "es" ? "Inicio" : "Start";
  const parent = parentCrumb(pathname, locale);
  const clean = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  const guide = guides.find((g) => pathFor(g.to, locale) === clean);
  const title = guide ? (locale === "sv" ? guide.label : guide.labelEn) : rawTitle;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: homeLabel, item: absoluteUrl(home) },
      ...(parent ? [{ "@type": "ListItem", position: 2, name: parent.label, item: absoluteUrl(parent.to) }] : []),
      { "@type": "ListItem", position: parent ? 3 : 2, name: title, item: absoluteUrl(pathname) },
    ],
  };
  return (
    <nav
      aria-label={locale === "en" ? "Breadcrumb" : locale === "es" ? "Migas de pan" : "Brödsmulor"}
      className="-mx-1 overflow-x-auto px-1 text-sm text-muted-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <ol className="flex min-w-0 flex-nowrap items-center gap-2 whitespace-nowrap">
        <li className="flex shrink-0 items-center">
          <Link
            to={home as never}
            className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
          >
            <Home className="size-4 shrink-0" aria-hidden />
            <span>{homeLabel}</span>
          </Link>
        </li>

        {parent ? (
          <>
            <li aria-hidden className="flex shrink-0 items-center text-muted-foreground/50">
              <ChevronRight className="size-4" />
            </li>
            <li className="flex shrink-0 items-center">
              <Link to={parent.to as never} className="transition-colors hover:text-foreground">
                {parent.label}
              </Link>
            </li>
          </>
        ) : null}

        <li aria-hidden className="flex shrink-0 items-center text-muted-foreground/50">
          <ChevronRight className="size-4" />
        </li>
        <li className="flex min-w-0 items-center">
          <span aria-current="page" className="truncate font-medium text-foreground">
            {title}
          </span>
        </li>
      </ol>


      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );
}

export function PageHero({
  eyebrow,
  title,
  intro,
  breadcrumbLabel,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  breadcrumbLabel?: string;
}) {
  const locale = useLocale();
  return (
    <section className="border-b border-border bg-sand">
      <div className="mx-auto max-w-3xl px-5 pt-6">
        <Breadcrumbs title={breadcrumbLabel ?? eyebrow} locale={locale} />
      </div>

      <div className="mx-auto max-w-3xl px-5 pt-6 pb-8 sm:pb-16">
        <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">
          {eyebrow}
        </p>
        <h1 className="mt-4 text-[1.9rem] leading-[1.12] font-semibold sm:text-5xl sm:leading-[1.08]">
          {title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:mt-5 sm:text-lg">
          {intro}
        </p>
      </div>
    </section>

  );
}


export function Prose({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl space-y-6 px-5 py-12 text-base leading-relaxed text-foreground/90 sm:py-16 [&_a]:link-quiet [&_a]:font-medium [&_a]:text-primary [&_h2]:pt-4 [&_h2]:text-2xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold [&_li]:leading-relaxed [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
      {children}
    </div>
  );
}

const CTA_DEFAULTS: Record<
  Locale,
  { title: string; text: string; apply: string; visit: string; fees: string; note: string }
> = {
  sv: {
    title: "Redo att ta nästa steg?",
    text: "Ansökningar tas emot löpande och det går bra att börja mitt i terminen.",
    apply: "Gör en ansökan",
    visit: "Boka ett besök",
    fees: "Se avgifter",
    note: "Inget bindande – vi svarar normalt inom en arbetsdag.",
  },
  en: {
    title: "Ready to take the next step?",
    text: "Applications are accepted on a rolling basis and it's fine to start mid-term.",
    apply: "Submit an application",
    visit: "Book a visit",
    fees: "See fees",
    note: "No commitment – we normally reply within one working day.",
  },
  es: {
    title: "¿Preparados para dar el siguiente paso?",
    text: "Aceptamos solicitudes durante todo el curso y es posible incorporarse a mitad del trimestre.",
    apply: "Enviar una solicitud",
    visit: "Reservar una visita",
    fees: "Ver tarifas",
    note: "Sin compromiso: normalmente respondemos en un día laborable.",
  },
};

export function CtaBand({
  title,
  text,
  locale,
}: {
  title?: string;
  text?: string;
  locale?: Locale;
}) {
  const urlLocale = useLocale();
  const loc = locale ?? urlLocale;
  const defaults = CTA_DEFAULTS[loc];
  return (
    <section className="mx-auto max-w-6xl px-5">
      <div className="rounded-xl bg-primary px-6 py-10 text-primary-foreground sm:px-12 sm:py-12">
        <h2 className="max-w-xl text-2xl font-semibold sm:text-3xl">{title ?? defaults.title}</h2>
        <p className="mt-3 max-w-xl leading-relaxed text-primary-foreground">
          {text ?? defaults.text}
        </p>
        <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
          <Link
            to={pathFor("/ansokan", loc) as never}
            className="flex min-h-11 items-center justify-center rounded-md bg-background px-5 text-sm font-medium text-primary transition-opacity hover:opacity-90"
          >
            {defaults.apply}
          </Link>
          <Link
            to={pathFor("/boka-besok", loc) as never}
            className="flex min-h-11 items-center justify-center rounded-md border border-primary-foreground/40 px-5 text-sm font-medium transition-colors hover:bg-primary-foreground/10"
          >
            {defaults.visit}
          </Link>
          <Link
            to={pathFor("/avgifter", loc) as never}
            className="text-center text-sm link-quiet sm:text-left"
          >
            {defaults.fees}
          </Link>
        </div>
        <p className="mt-4 text-sm text-primary-foreground">
          {defaults.note}{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">
            info@sweintschool.com
          </a>
        </p>
      </div>
    </section>
  );
}

/**
 * Layout för policysidor (cookiepolicy, integritet, villkor, uppförandekod):
 * innehållsförteckning byggd från rubrikerna och texten i ett luftigt kort.
 * data-policy-body används av policyarkivet i admin för att spara exakt text.
 */
export function PolicyBody({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const [toc, setToc] = useState<{ id: string; text: string }[]>([]);
  const locale = useLocale();
  useEffect(() => {
    const hs = Array.from(ref.current?.querySelectorAll("h2") ?? []);
    setToc(
      hs.map((h, i) => {
        if (!h.id) h.id = `avsnitt-${i + 1}`;
        return { id: h.id, text: h.textContent ?? "" };
      }),
    );
  }, []);
  const tocLabel = locale === "en" ? "Contents" : locale === "es" ? "Contenido" : "Innehåll";
  return (
    <div className="mx-auto grid max-w-6xl gap-8 px-5 py-10 sm:py-14 lg:grid-cols-[220px_minmax(0,1fr)]">
      <nav aria-label={tocLabel} className="lg:sticky lg:top-24 lg:self-start">
        {toc.length > 1 && (
          <details className="group rounded-xl border border-border bg-card p-4 lg:border-0 lg:bg-transparent lg:p-0" open>
            <summary className="cursor-pointer list-none text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{tocLabel}</summary>
            <ol className="mt-3 space-y-1.5 border-l border-border text-sm">
              {toc.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="-ml-px block border-l-2 border-transparent py-0.5 pl-3 text-foreground/75 hover:border-primary hover:text-primary">
                    {t.text}
                  </a>
                </li>
              ))}
            </ol>
          </details>
        )}
      </nav>
      <article
        ref={ref}
        data-policy-body
        className="policy-body min-w-0 rounded-2xl border border-border bg-card px-6 py-8 text-base leading-[1.75] text-foreground/85 shadow-sm sm:px-10 sm:py-12"
      >
        {children}
      </article>
    </div>
  );
}
